/**
 * Proactive Token Refresh Component
 * Automatically refreshes access tokens before they expire
 *
 * Features:
 * - Circuit breaker pattern to prevent refresh storms
 * - Exponential backoff on failures
 * - Skips entirely in dev mode (dev token handles its own refresh)
 * - Stops after max failures to prevent rate limiting
 */

import { useEffect, useRef } from 'react';
import {
  isTokenExpiringSoon,
  setAuthToken,
  getAuthToken,
  getTokenExpiration,
  clearAuthTokens,
} from '@/lib/auth-utils';
import { isDevelopment } from '@/config/environment';

export function ProactiveTokenRefresh() {
  const intervalRef = useRef<number | null>(null);
  const failureCountRef = useRef<number>(0);
  const lastAttemptRef = useRef<number>(0);
  const MAX_FAILURES = 3; // Stop trying after 3 consecutive failures
  const MIN_RETRY_INTERVAL = 60000; // Minimum 1 minute between attempts
  const isStoppedRef = useRef<boolean>(false);

  useEffect(() => {
    // SKIP ENTIRELY in dev mode - dev token endpoint handles refresh
    if (isDevelopment) {
      // Removed console.log for production security (no dev mode information leakage)
      return;
    }

    // Check every minute if token needs refresh
    intervalRef.current = window.setInterval(async () => {
      // Stop trying if we've had too many failures (invalid token)
      if (failureCountRef.current >= MAX_FAILURES || isStoppedRef.current) {
        if (!isStoppedRef.current) {
          console.warn('Token refresh disabled after repeated failures. Clearing invalid tokens.');
          isStoppedRef.current = true;

          // Clear invalid tokens to prevent continued failures
          clearAuthTokens();

          // Clear the interval to stop further attempts
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          // Redirect to login in production (not in dev mode)
          if (!isDevelopment && window.location.pathname !== '/sign-in') {
            window.location.href = '/sign-in';
          }
        }
        return;
      }

      // Circuit breaker: Don't retry too quickly (prevent storms)
      const now = Date.now();
      const timeSinceLastAttempt = now - lastAttemptRef.current;
      if (timeSinceLastAttempt < MIN_RETRY_INTERVAL) {
        return; // Too soon, skip this interval
      }

      const token = getAuthToken();
      if (!token) {
        return; // No token, nothing to refresh
      }

      // Verify we can actually read the token expiration
      const exp = getTokenExpiration(token);
      if (!exp) {
        // Can't read expiration - token may be malformed, don't spam refresh

        console.warn('Unable to read token expiration. Skipping proactive refresh.');
        return;
      }

      // Check if token will expire in next 5 minutes
      if (isTokenExpiringSoon(300)) {
        // Record attempt timestamp
        lastAttemptRef.current = Date.now();

        try {
          // SECURITY: Refresh token is sent via httpOnly cookie (automatic)
          const response = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // CRITICAL: Include cookies in request
          });

          if (response.ok) {
            const data = await response.json();

            // SECURITY: Server sets new refresh token as httpOnly cookie
            // We only need to store the access token
            if (data.accessToken) {
              setAuthToken(data.accessToken);
              failureCountRef.current = 0; // Reset failure count on success
              // Removed console.log for production security (no token timing information leakage)
            }
          } else if (response.status === 401) {
            // 401 = invalid/expired refresh token
            failureCountRef.current++;

            console.error(
              `Token refresh failed with 401 (attempt ${failureCountRef.current}/${MAX_FAILURES})`,
            );

            // If max failures reached, clear invalid tokens and stop
            if (failureCountRef.current >= MAX_FAILURES) {
              console.warn('Clearing invalid authentication tokens. Please log in again.');
              clearAuthTokens();
              isStoppedRef.current = true;

              // Clear the interval to stop further attempts
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
            }
          } else if (response.status === 429) {
            // Rate limited - exponential backoff
            failureCountRef.current++;

            console.warn(
              `Token refresh rate limited (attempt ${failureCountRef.current}/${MAX_FAILURES}). Backing off.`,
            );

            // Increase retry interval exponentially
            lastAttemptRef.current =
              Date.now() + MIN_RETRY_INTERVAL * Math.pow(2, failureCountRef.current);
          } else {
            // Other error - count as failure but don't clear tokens yet
            failureCountRef.current++;

            console.error(
              `Failed to refresh token: ${response.status} (attempt ${failureCountRef.current}/${MAX_FAILURES})`,
            );
          }
        } catch (error) {
          failureCountRef.current++;

          console.error(
            `Error during proactive token refresh (attempt ${failureCountRef.current}/${MAX_FAILURES}):`,
            error,
          );
        }
      }
    }, 60000); // Check every minute

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // This component doesn't render anything
  return null;
}
